import { Component, OnInit, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, filter, map, take } from 'rxjs';
import { CitadelConfigurationService, CitadelErrorLoggingService } from '@citadel/core';
import { AuthenticationType } from '../../models/authentication-type.model';
import { SignInService } from '../../services/sign-in-service';

/**
 * Sign-in's shell: the greeting, logo and step indicator around the step on its child route. Each
 * step's route gives its place in the indicator as `data.step`. Sign-in needs the
 * return URL from sessionStorage; without one it sends the user back to the app, which returns
 * through sign-in-redirector with a fresh one.
 */
@Component({
  selector: 'app-sign-in-page',
  templateUrl: './sign-in-page.component.html',
  styleUrls: ['./sign-in-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SignInPageComponent implements OnInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private signInService = inject(SignInService);
  private config = inject(CitadelConfigurationService);
  private errorLogging = inject(CitadelErrorLoggingService);

  private readonly routeStep = toSignal(
    this.router.events.pipe(filter(event => event instanceof NavigationEnd), map(() => this.stepIndex())),
    { initialValue: this.stepIndex() });
  // EmailOtp accounts have no password, so their indicator drops that step.
  private readonly isEmailOtp = computed(() => this.signInService.account()?.authenticationTypeL === AuthenticationType.EmailOtp);
  protected readonly selectedIndex = computed(() => this.isEmailOtp() && this.routeStep() > 1 ? this.routeStep() - 1 : this.routeStep());
  private readonly emailStep = { title: new BehaviorSubject<string>('Email') };
  private readonly passwordStep = { title: new BehaviorSubject<string>('Password') };
  private readonly otpStep = { title: new BehaviorSubject<string>('One-time-PIN') };
  protected readonly steps = computed<any[]>(() => this.isEmailOtp()
    ? [this.emailStep, this.otpStep]
    : [this.emailStep, this.passwordStep, this.otpStep]);

  constructor() {
    console.log('Auth server version: 2.0.0');
    this.errorLogging.logError('auth-redirect-debug', `SignInPageComponent: Loading sign-in context from sessionStorage`);
  }

  ngOnInit() {
    const returnUrl = this.signInService.returnUrl();
    if (returnUrl) {
      this.errorLogging.logError('auth-redirect-debug', `SignInPageComponent: returnUrl found: ${returnUrl}, client_id: ${this.signInService.clientId()}, impersonate: ${this.signInService.impersonate()}`);
    } else {
      this.errorLogging.logError('auth-redirect-debug', `SignInPageComponent: No returnUrl found in sessionStorage, navigating to app`);
      this.navigateToApp();
    }
  }

  private stepIndex(): number {
    return this.route.snapshot.firstChild?.data['step'] ?? 0;
  }

  private navigateToApp() {
    this.config.getGlobalSettingsLoaded().pipe(take(1)).subscribe(settings => {
      // BUG (found adopting strict mode, not fixed here): IAppSettings.rerouteAddress is declared
      // `string`, not a per-hostname map, so this index access can never resolve a real value at
      // runtime as written - see the identical site in app.component.ts.
      const rerouteAddress = (settings.rerouteAddress as unknown as Record<string, string> | undefined)?.[window.location.hostname];
      if (!!rerouteAddress) {
        window.location.href = rerouteAddress;
      } else {
        // This can only be used once the auth service runs under the main application
        //this.router.navigate(['..'], { relativeTo: this.route });
      }
    });
  }
}
